<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import { encumbranceOptions } from '../composables/salesPartyFormOptions';
import SalesPropertyReferenceField from './SalesPropertyReferenceField.vue';

const props = defineProps({
  profile: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
  partyRole: { type: String, default: 'vendedor' },
});

const emit = defineEmits(['update']);
const { t } = useI18n();
const encumbrances = computed(() => props.profile.encumbrances || []);
const showOptions = ref(Boolean(props.profile.encumbrances_enabled));
const options = computed(() => encumbranceOptions(t));
const selectedIds = computed(() =>
  encumbrances.value.map(item => String(item.id))
);

watch(
  encumbrances,
  values => {
    if (values.length && !showOptions.value) showOptions.value = true;
  },
  { immediate: true }
);

const toggle = value => {
  showOptions.value = value;
  emit('update', 'encumbrances_enabled', value);
  if (!value) emit('update', 'encumbrances', []);
};

const toggleOptions = () => toggle(!showOptions.value);

const updateSelection = values => {
  const next = values.map(value => {
    const id = String(value);
    return (
      encumbrances.value.find(item => String(item.id) === id) || {
        id,
        reference_id: '',
      }
    );
  });
  emit('update', 'encumbrances', next);
};
</script>

<template>
  <section
    data-test="sales-party-encumbrances-section"
    class="rounded-[10px] bg-white p-8 font-roboto shadow-sm max-[700px]:p-0"
  >
    <div class="grid w-full" :data-party-role="partyRole">
      <header>
        <h2
          data-test="sales-party-encumbrances-title"
          class="max-w-[832px] font-lato text-[32px] font-bold leading-[39px] text-[#464F53] max-[700px]:max-w-[320px] max-[700px]:text-[24px] max-[700px]:leading-[29px]"
        >
          {{ t('SALES_PARTIES.ENCUMBRANCES.DESCRIPTION') }}
        </h2>
      </header>

      <button
        type="button"
        data-test="sales-party-encumbrances-toggle"
        :aria-pressed="showOptions"
        class="mt-9 flex w-fit items-center gap-3 rounded-[10px] bg-[#EDF8F7] px-3 py-1.5 text-base leading-6 text-[#009F94]"
        @click="toggleOptions"
      >
        <span
          class="relative inline-flex h-3 w-9 shrink-0 items-center rounded-full bg-[#8AD8D1]"
          aria-hidden="true"
        >
          <span
            class="absolute left-0 size-5 rounded-full bg-[#009F94] shadow-sm transition-transform"
            :class="showOptions ? 'translate-x-4' : 'translate-x-0'"
          />
        </span>
        <span>{{ t('SALES_PARTIES.ENCUMBRANCES.ENABLED') }}</span>
      </button>

      <SalesPropertyReferenceField
        v-if="showOptions"
        class="mt-[51px]"
        test-id="sales-party-encumbrances-field"
        :label="`${t('SALES_PARTIES.ENCUMBRANCES.TYPE')}*`"
        :filled="selectedIds.length > 0"
        :error="errors.encumbrances"
        indicator="select"
        :show-success="false"
      >
        <TagMultiSelectComboBox
          listbox-id="sales-party-encumbrances"
          reference-style
          :model-value="selectedIds"
          :options="options"
          :placeholder="t('SALES_PARTIES.ENCUMBRANCES.TYPE_PLACEHOLDER')"
          :has-error="Boolean(errors.encumbrances)"
          @update:model-value="updateSelection"
        />
      </SalesPropertyReferenceField>
    </div>
  </section>
</template>
